import { useMutation, useQuery } from '@tanstack/react-query';
import { loadDealerData, loadEnrollmentData, saveDealerData, saveEnrollmentData } from '../../utils/offlineCache';
import { getActionRequiredWatch, getActionRequiredWatchHistory } from './actionRequiredApi';
import { fetchDealerProgramSelections, getDealerCleanHistory, getDealerHistory, singleDealerLookup } from './dealerApi';
import { getDailyDigest, getDailyDigestSection } from './dailyDigestApi';
import { getEnrollmentByProgram, getEnrollmentForAllPrograms, getSnapshots } from './enrollmentApi';
import {
    getEnrollmentStatusChangeMetrics,
    getEnrollmentStatusChanges,
    getEnrollmentStatusChangeSeries,
    getStatusChangeMetrics,
    getStatusChanges,
} from './statusChangeApi';
import { mergeEnrollmentRow, normalizeEnrollmentBac } from '../../utils/enrollmentRowMerge';

export const createEnrollmentHooks = ({ queryClient }) => {
    const useEnrollmentData = (programName) =>
        useQuery({
            queryKey: ['enrollmentData', { type: 'program', value: programName }],
            queryFn: async () => {
                if (!navigator.onLine && programName) {
                    try {
                        const cached = await loadEnrollmentData(programName);
                        if (cached && Array.isArray(cached)) {
                            return cached;
                        }
                    } catch {
                        // Ignore cache errors, fall through to API call
                    }
                }

                const data = await getEnrollmentByProgram(programName);
                if (data && Array.isArray(data) && programName) {
                    await saveEnrollmentData(programName, data).catch(() => {
                        // Silently fail if caching fails
                    });
                }
                return data;
            },
            enabled: !!programName,
            staleTime: 0,
            retry: (failureCount) => {
                if (!navigator.onLine) return false;
                return failureCount < 3;
            },
        });

    const useAllEnrollmentData = () =>
        useQuery({
            queryKey: ['allEnrollmentData'],
            queryFn: async () => {
                const data = await getEnrollmentForAllPrograms();
                if (data && Array.isArray(data)) {
                    data.forEach(async (programResult) => {
                        if (programResult.programName && Array.isArray(programResult.data)) {
                            await saveEnrollmentData(programResult.programName, programResult.data).catch(() => {
                                // Silently fail if caching fails
                            });
                        }
                    });
                }
                return data;
            },
            staleTime: 0,
            onSuccess: (data) => {
                if (data && Array.isArray(data)) {
                    data.forEach((programResult) => {
                        const queryKey = ['enrollmentData', { type: 'program', value: programResult.programName }];
                        queryClient.setQueryData(queryKey, programResult.data);
                    });
                }
            },
            retry: (failureCount) => {
                if (!navigator.onLine) return false;
                return failureCount < 3;
            },
        });

    const useSnapshots = () =>
        useQuery({
            queryKey: ['snapshots'],
            queryFn: getSnapshots,
            staleTime: 5 * 60 * 1000,
        });

    const useActionRequiredWatch = (options = {}) =>
        useQuery({
            queryKey: [
                'actionRequiredWatch',
                { programName: options.programName || null, minDaysRemaining: options.minDaysRemaining ?? null },
            ],
            queryFn: () => getActionRequiredWatch(options),
            staleTime: 15 * 1000,
            keepPreviousData: true,
        });

    const useActionRequiredHistory = (options = {}) =>
        useQuery({
            queryKey: ['actionRequiredWatchHistory', { programName: options.programName || null, days: options.days ?? 90 }],
            queryFn: () => getActionRequiredWatchHistory(options),
            staleTime: 60 * 1000,
            keepPreviousData: true,
        });

    const useDailyDigest = (options = {}) =>
        useQuery({
            queryKey: ['dailyDigest', { days: options.days ?? 7 }],
            queryFn: () => getDailyDigest(options),
            staleTime: 60 * 1000,
        });

    const useDailyDigestSection = (options = {}) =>
        useQuery({
            queryKey: [
                'dailyDigestSection',
                {
                    section: options.section || null,
                    days: options.days ?? null,
                    programName: options.programName || null,
                    bac: options.bac || null,
                    rowKey: options.rowKey || null,
                    search: options.search || null,
                    level: options.level || null,
                    limit: options.limit ?? null,
                    offset: options.offset ?? null,
                },
            ],
            queryFn: () => getDailyDigestSection(options),
            enabled: Boolean(options.section),
            keepPreviousData: true,
            staleTime: 60 * 1000,
        });

    const useStatusChangeMetrics = (options = {}) =>
        useQuery({
            queryKey: ['statusChangeMetrics'],
            queryFn: getStatusChangeMetrics,
            refetchInterval: options.refetchInterval ?? 60 * 1000,
        });

    const useStatusChanges = (options = {}) =>
        useQuery({
            queryKey: ['statusChanges', options],
            queryFn: () => getStatusChanges(options),
            staleTime: 30 * 1000,
            keepPreviousData: true,
        });

    const useEnrollmentStatusChangeMetrics = (options = {}) =>
        useQuery({
            queryKey: ['enrollmentStatusChangeMetrics', options],
            queryFn: () => getEnrollmentStatusChangeMetrics(options),
            staleTime: 60 * 1000,
            keepPreviousData: true,
        });

    const useEnrollmentStatusChanges = (options = {}) =>
        useQuery({
            queryKey: ['enrollmentStatusChanges', options],
            queryFn: () => getEnrollmentStatusChanges(options),
            staleTime: 30 * 1000,
            keepPreviousData: true,
        });

    const useEnrollmentStatusChangeSeries = (options = {}) =>
        useQuery({
            queryKey: ['enrollmentStatusChangeSeries', options],
            queryFn: () => getEnrollmentStatusChangeSeries(options),
            staleTime: 60 * 1000,
            keepPreviousData: true,
        });

    const useSingleDealerSearch = () =>
        useMutation({
            mutationFn: singleDealerLookup,
        });

    const useDealerHistory = (bac, programName) =>
        useQuery({
            queryKey: ['dealerHistory', { bac, programName }],
            queryFn: async () => {
                if (!navigator.onLine && bac) {
                    try {
                        const cached = await loadDealerData(bac);
                        if (cached) return cached;
                    } catch {
                        // Ignore cache errors
                    }
                }

                const data = await getDealerHistory(bac, programName);
                if (data && bac) {
                    await saveDealerData(bac, data).catch(() => {
                        // Silently fail if caching fails
                    });
                }
                return data;
            },
            enabled: !!bac,
            staleTime: 5 * 60 * 1000,
            retry: (failureCount) => {
                if (!navigator.onLine) return false;
                return failureCount < 3;
            },
        });

    const useDealerCleanHistory = (bac, programName) =>
        useQuery({
            queryKey: ['dealerCleanHistory', { bac, programName }],
            queryFn: () => getDealerCleanHistory(bac, programName),
            enabled: !!bac,
            staleTime: 5 * 60 * 1000,
            retry: (failureCount) => {
                if (!navigator.onLine) return false;
                return failureCount < 3;
            },
        });

    const useRefreshDealerSelections = () =>
        useMutation({
            mutationFn: fetchDealerProgramSelections,
            onSuccess: (payload, variables) => {
                const programName = variables?.programName;
                const bac = variables?.bac != null ? String(variables.bac).trim() : '';
                if (!programName || !bac) return;

                const programQueryKey = ['enrollmentData', { type: 'program', value: programName }];

                queryClient.setQueryData(programQueryKey, (existing) => {
                    if (!Array.isArray(existing)) return existing;
                    let changed = false;
                    const next = existing.map((row) => {
                        if (!row) return row;
                        const rowBac = normalizeEnrollmentBac(row);
                        if (rowBac !== bac) return row;
                        changed = true;
                        return mergeEnrollmentRow(row, payload);
                    });
                    return changed ? next : existing;
                });

                queryClient.setQueryData(['allEnrollmentData'], (existing) => {
                    if (!Array.isArray(existing)) return existing;
                    let changed = false;
                    const next = existing.map((entry) => {
                        if (!entry || entry.programName !== programName || !Array.isArray(entry.data)) return entry;
                        let localChanged = false;
                        const updatedData = entry.data.map((row) => {
                            if (!row) return row;
                            const rowBac = normalizeEnrollmentBac(row);
                            if (rowBac !== bac) return row;
                            localChanged = true;
                            return mergeEnrollmentRow(row, payload);
                        });
                        if (!localChanged) return entry;
                        changed = true;
                        return { ...entry, data: updatedData };
                    });
                    return changed ? next : existing;
                });
            },
        });

    return {
        useEnrollmentData,
        useAllEnrollmentData,
        useSnapshots,
        useActionRequiredWatch,
        useActionRequiredHistory,
        useDailyDigest,
        useDailyDigestSection,
        useStatusChangeMetrics,
        useStatusChanges,
        useEnrollmentStatusChangeMetrics,
        useEnrollmentStatusChanges,
        useEnrollmentStatusChangeSeries,
        useSingleDealerSearch,
        useDealerCleanHistory,
        useDealerHistory,
        useRefreshDealerSelections,
    };
};
